import BackgroundTransitions, {
  Background,
  useGameState,
} from "containers/Game/BackgroundTransitions";
import { actions, gameStateType } from "core/constants";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Content from "./Content";
import { maybeGetTestGameSession } from "utils/sessionHelper";
import GameHistoryMobile, {
  OpenGameHistoryButton,
  useGameEventLog,
} from "pages/GameHistoryMobile";

const {
  START_GAME,
  CREATE_PLAYER,
  CREATE_SESSION,
  FIND_PLAYER,
  FIND_SESSION,
  ENDED,
  PLAYER_INFO,
  PLAYER_ADDED,
  UPDATED_SESSION_GAME_STATE,
  UPDATE_GAME_SETTINGS,
  UPDATED_GAME_SETTINGS,
  NEW_GAME,
  FRESH_START,
} = actions;
const {
  WAITING,
  INTRO,
  READY,
  STARTED,
  HUMAN_WIN,
  VAMPIRE_WIN,
  NIGHT_INTRO,
  NIGHT,
  MORNING_INTRO,
  MORNING,
  DAY,
  POLLS,
} = gameStateType;

const PLAYER_NAME = "Player Name";
const PLAYER_ID = "Player Id";
const GAME_SESSION_ID = "Game Session Id";

const Home = ({ loading, socket }) => {
  const location = useLocation() || {};
  const history = useHistory() || {};
  const gameSessionHash = location?.hash?.slice(1);
  if (gameSessionHash) {
    localStorage.setItem(GAME_SESSION_ID, gameSessionHash);
  }
  // Use gameSessionHash directly from location instead of localStorage
  // This ensures gameSessionId is reactive to hash changes
  const _gameSessionId =
    gameSessionHash || localStorage.getItem(GAME_SESSION_ID);
  const gameSessionId = maybeGetTestGameSession(_gameSessionId);
  const [gameSessionValue, setGameSessionValue] = useState("");
  const [gameSession, setGameSession] = useState(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  const resetGameSession = useCallback(
    (e) => {
      e && e.preventDefault();
      history.replace("#");
      localStorage.removeItem(GAME_SESSION_ID);
      localStorage.removeItem(PLAYER_ID);
      localStorage.removeItem(PLAYER_NAME);
      setGameSession(null);
      setPlayerId(null);
      setPlayer(null);
      setPlayers([]);
      setPlayerNameValue("");
    },
    [history]
  );
  const _setGameSessionValue = (value) => {
    if (gameSessionId) {
      resetGameSession();
    }
    setGameSessionValue(value);
  };

  const [playerError, setPlayerError] = useState("");
  const [playerNameValue, setPlayerNameValue] = useState(
    localStorage.getItem(PLAYER_NAME) || ""
  );
  const _setPlayerNameValue = (value) => {
    // TODO: Bug fix... value ++ "spaces" creates "."
    if (value) {
      setPlayerNameValue(value);
      setPlayerError("");
    }
  };
  const [playerId, setPlayerId] = useState(localStorage.getItem(PLAYER_ID));
  const [player, setPlayer] = useState(null);
  const _setPlayerId = (_playerId) => {
    localStorage.setItem(PLAYER_ID, _playerId);
    setPlayerId(_playerId);
  };

  // Game settings state
  const [gameSettings, setGameSettings] = useState(null);

  // Players state for waiting room
  const [players, setPlayers] = useState([]);

  const _setPlayerName = (e) => {
    if (e) {
      e.preventDefault();
    }
    const trimmedName = playerNameValue.trim();
    setPlayerNameValue(trimmedName || "");
    localStorage.setItem(PLAYER_NAME, trimmedName);
    socket.emit(
      CREATE_PLAYER,
      {
        gameSessionId,
        playerName: trimmedName,
        playerId, // Send existing playerId to verify ownership
      },
      (_player) => {
        console.log("🔵 Home: CREATE_PLAYER cb", {
          status: _player?.status,
          playerData: _player?.data,
        });
        if (_player.status === 403) {
          setPlayerError(_player.error || _player.warn);
          resetGameSession();
          // Hack! (to show warning even though player input no longer exists)
          setGameSession({ warn: _player.warn });
        }
        if (_player.status !== 200) {
          setPlayerError(_player.error || _player.warn);
        } else {
          const newPlayerId = _player?.data?.id || "";
          _setPlayerId(newPlayerId);
          // After successful player creation, re-fetch session to get current settings
          try {
            if (typeof window !== "undefined") {
              window.__homePostCreatePlayerFetch = {
                gameSessionId,
                playerId: newPlayerId,
              };
            }
          } catch (_) {}
          socket.emit(
            FIND_SESSION,
            {
              gameSessionId,
              playerId: newPlayerId,
            },
            (_gameSession) => {
              console.log("🔵 Home: FIND_SESSION after CREATE_PLAYER", {
                status: _gameSession?.status,
                players: _gameSession?.data?.players,
                playersCount: _gameSession?.data?.players?.length,
                fullData: _gameSession?.data,
              });
              try {
                if (typeof window !== "undefined") {
                  window.__homePostCreatePlayerFetchCb = {
                    status: _gameSession?.status,
                    settings: _gameSession?.data?.settings || null,
                    gameState: _gameSession?.data?.gameState,
                  };
                }
              } catch (_) {}
              if (_gameSession?.data?.settings) {
                setGameSettings(_gameSession.data.settings);
              }
              // Extract players if available
              if (_gameSession?.data?.players) {
                console.log("👥 Home: setPlayers after CREATE_PLAYER", {
                  playersCount: _gameSession.data.players.length,
                });
                setPlayers(_gameSession.data.players);
              }
            }
          );
        }
      }
    );
  };

  const onSubmitGameSession = useCallback(
    (e) => {
      e.preventDefault();
      if (gameSessionValue) {
        history.replace("#" + gameSessionValue);
      }
    },
    [gameSessionValue, history]
  );

  useEffect(() => {
    if (!loading && gameSessionId) {
      console.log("🔔 Home: FIND_SESSION emit", { gameSessionId, playerId });
      try {
        if (typeof window !== "undefined") {
          window.__homeFindSessionEmit = { gameSessionId, playerId };
        }
      } catch (_) {}
      socket.emit(
        FIND_SESSION,
        {
          gameSessionId,
          playerId,
        },
        (_gameSession) => {
          try {
            if (typeof window !== "undefined") {
              window.__homeFindSessionCb = {
                status: _gameSession?.status,
                settings: _gameSession?.data?.settings || null,
                gameState: _gameSession?.data?.gameState,
              };
            }
          } catch (_) {
            console.warn("Failed to set window.__homeFindSessionCb");
          }
          if (_gameSession.status === 403) {
            resetGameSession();
          }
          console.log("TEST: Home: FIND_SESSION cb", _gameSession);
          setGameSession(_gameSession);
          setIsCreatingSession(false);
          // Extract settings from session
          if (_gameSession?.data?.settings) {
            console.log("📝 Home: setGameSettings from FIND_SESSION");
            try {
              if (typeof window !== "undefined") {
                window.__homeSetGameSettingsFromFind =
                  _gameSession.data.settings;
              }
            } catch (_) {}
            setGameSettings(_gameSession.data.settings);
          }
          // Extract players from session
          if (_gameSession?.data?.players) {
            console.log("👥 Home: setPlayers from FIND_SESSION", {
              playersCount: _gameSession.data.players.length,
            });
            setPlayers(_gameSession.data.players);
          }
        }
      );
    }
  }, [socket, gameSessionId, loading, playerId, resetGameSession]);
  useEffect(() => {
    let playerInfoKey = null;
    if (playerId) {
      socket.emit(FIND_PLAYER, {
        playerId,
      });
      playerInfoKey = `${playerId}-${PLAYER_INFO}`;
      socket.on(playerInfoKey, function (_player) {
        setPlayer(_player);
      });
    }
  }, [playerId, socket]);

  const hasSessionError = !!gameSession?.error || !!gameSession?.warn;

  let validGameSession = gameSession?.status === 200;
  // Kick players out when they are in the lobby - and game starts with out 'em.
  if (
    gameSession?.data?.gameState &&
    gameSession?.data?.gameState !== WAITING &&
    !player
  ) {
    validGameSession = false;
  }
  useEffect(() => {
    let updateSessionGameStateKey = null;
    let updatedGameSettingsKey = null;
    let playerAddedKey = null;
    let endedKey = null;
    if (gameSessionId) {
      // Register settings listener ALWAYS (not conditional on validGameSession)
      // so new players receive settings broadcast even before session validation completes
      updatedGameSettingsKey = `${gameSessionId}-${UPDATED_GAME_SETTINGS}`;
      socket.on(updatedGameSettingsKey, function ({ settings }) {
        console.log("🔶 Home: UPDATED_GAME_SETTINGS received", { settings });
        try {
          if (typeof window !== "undefined") {
            window.__homeUpdatedSettings = settings || null;
          }
        } catch (_) {}
        setGameSettings(settings);
      });

      // Register player added listener
      playerAddedKey = `${gameSessionId}-${PLAYER_ADDED}`;

      // Store socket info for debugging
      try {
        if (typeof window !== "undefined") {
          window.__homeSocketInfo = {
            socketId: socket.id,
            connected: socket.connected,
            listenerEvent: playerAddedKey,
          };
        }
      } catch (_) {}

      socket.on(playerAddedKey, function (player) {
        console.log("👥 Home: PLAYER_ADDED EVENT RECEIVED!", { player });
        try {
          if (typeof window !== "undefined") {
            window.__homePlayerAddedReceived =
              window.__homePlayerAddedReceived || [];
            window.__homePlayerAddedReceived.push(player);
          }
        } catch (_) {}
        setPlayers((p) => {
          console.log(
            "👥 Home: setPlayers called, current:",
            p,
            "adding:",
            player
          );
          return p.concat(player);
        });
      });
    }
    if (gameSessionId && validGameSession) {
      updateSessionGameStateKey = `${gameSessionId}-${UPDATED_SESSION_GAME_STATE}`;
      socket.on(updateSessionGameStateKey, function (response) {
        const {
          gameState,
          message,
          condemned,
          players: updatedPlayers,
        } = response;
        console.log("TEST: Home: UPDATED_SESSION_GAME_STATE received", {
          gameState,
          message,
          condemned,
          updatedPlayers,
        });
        if (gameState) {
          setGameSession((g) => ({
            ...g,
            data: { ...g?.data, gameState, message, condemned },
          }));
        }
        if (updatedPlayers) {
          setPlayers(updatedPlayers);
        }
      });
    }
    if (!hasSessionError && validGameSession) {
      endedKey = `${gameSessionId}-${ENDED}`;
      socket.on(endedKey, function (_gameSession) {
        const actualGameSession = maybeGetTestGameSession(_gameSession);
        setGameSession(actualGameSession);
        // Not resetting _playerId in this scenario sot that useEffect above isn't triggered
        // _setPlayerId('');
        setPlayer(null);
        setPlayers([]);
        socket.off(updateSessionGameStateKey);
        socket.off(updatedGameSettingsKey);
        socket.off(playerAddedKey);
        socket.off(endedKey);
      });
    }
    // cleanUp
    return () => {
      [
        updateSessionGameStateKey,
        updatedGameSettingsKey,
        playerAddedKey,
        endedKey,
      ].forEach((key) => {
        if (key) {
          socket.off(key);
        }
      });
    };
  }, [socket, gameSessionId, hasSessionError, validGameSession]);

  const gameState = gameSession?.data?.gameState || WAITING;
  // const message = gameSession?.data?.message; // Message sent to desktop, if ever needed

  const startGame = () => {
    socket.emit(START_GAME, { gameSessionId, skipIntro: true });
  };

  const startGameWithIntro = () => {
    socket.emit(START_GAME, { gameSessionId, skipIntro: false });
  };

  const newGameAddPlayers = () => {
    socket.emit(NEW_GAME, { gameSessionId });
  };
  const quitRound = () => {
    socket.emit(FRESH_START, { gameSessionId });
    // Clear history FIRST before clearing state to avoid flash
    history.replace("#");
    // Clear all localStorage data
    localStorage.removeItem(GAME_SESSION_ID);
    localStorage.removeItem(PLAYER_ID);
    localStorage.removeItem(PLAYER_NAME);
    setGameSession(null);
    setPlayerId(null);
    setPlayer(null);
    setPlayers([]);
    setPlayerNameValue("");
  };

  const handleSaveSettings = (newSettings) => {
    socket.emit(
      UPDATE_GAME_SETTINGS,
      {
        gameSessionId,
        settings: newSettings,
      },
      (response) => {
        if (response.status !== 200) {
          alert(response.error);
        }
      }
    );
  };

  const waiting = gameState === WAITING || player === null;
  // returning value, and using later. hacky
  const isIntro = [INTRO, NIGHT_INTRO, MORNING_INTRO].find(
    (intro) => intro === gameState
  );

  const hasStarted = gameState === STARTED;
  const isNight = gameState === NIGHT && player && !player.expired;
  const isMorning = gameState === MORNING && player && !player.expired;
  const isDay = gameState === DAY && player && !player.expired;
  const isPolls = gameState === POLLS && player && !player.expired;
  const humanWin = gameState === HUMAN_WIN && player;
  const vampireWin = gameState === VAMPIRE_WIN && player;

  const gameSessionError = gameSession?.error || gameSession?.warn;

  // Calculate vipReady based on player count vs minPlayers
  const minPlayers = gameSettings?.roleConfig?.minPlayers || 6;
  const vipReady = !!player?.vip && players.length >= minPlayers;

  // const randomImage = player?.image && imageObj[player.image];
  const showLoginTransition = gameSessionId && player?.userName;
  const gameContainerDependencies = {
    gameState,
    loading,
    vipReady,
    validGameSession,
    showLoginTransition,
    preGame: waiting,
    playerVoted: !!player?.voted,
    role: player?.role,
    image: player?.image,
    isExpired: !!player?.expired,
    message: player?.message || "",
    isIntro,
    hasStarted,
    isNight,
    isMorning,
    isDay,
    isPolls,
    vampireWin,
    humanWin,
  };
  const {
    delayedProps,
    animationState,
    setNextAnimation,
    setHasTransitionImgLoaded,
    hasTransitionImgLoaded,
  } = useGameState(gameContainerDependencies);
  // Calculate isVIP (player is VIP if their ID matches a VIP player in the session)
  const isVIP = player?.vip || false;

  const contentProps = {
    socket,
    delayedProps,
    gameSession,
    onSubmitGameSession,
    onCreateSession: (_sessionName) => {
      const sessionName = maybeGetTestGameSession(_sessionName);
      setIsCreatingSession(true);
      // Create the session on the backend
      socket.emit(CREATE_SESSION, { sessionName }, (response) => {
        if (response.status === 200 || response.status === 201) {
          // Set session state immediately so validGameSession is true
          setGameSession(response);
          // Session created successfully, set the hash to the ACTUAL gameSessionId
          // (which may have _TEST suffix in test mode)
          history.replace("#" + response.data.gameSessionId);
          // Extract settings from created session
          if (response?.data?.settings) {
            setGameSettings(response.data.settings);
          }
        } else {
          setIsCreatingSession(false);
        }
      });
    },
    _setGameSessionValue,
    gameSessionError,
    resetGameSession,
    gameSessionId,
    _setPlayerName,
    _setPlayerNameValue,
    playerNameValue,
    playerError,
    startGame,
    startGameWithIntro,
    player,
    players,
    newGameAddPlayers,
    quitRound,
    // Settings props for WaitingRoom
    gameSettings,
    handleSaveSettings,
    isVIP,
    isSettingsLocked: gameState !== WAITING,
    isCreatingSession,
  };

  const [eventLog, canShowHistory] = useGameEventLog(
    socket,
    gameSessionId,
    player,
    gameState
  );
  const [showHistory, setShowHistory] = useState(false);

  // This is magic
  return (
    <>
      <BackgroundTransitions
        animationState={animationState}
        setNextAnimation={setNextAnimation}
        setHasTransitionImgLoaded={setHasTransitionImgLoaded}
      />
      <Background
        isDay={[MORNING_INTRO, MORNING, DAY, POLLS, HUMAN_WIN].includes(
          delayedProps.gameState
        )}
        showAnimation={delayedProps.isExpired}
      />

      {showHistory && canShowHistory && (
        <GameHistoryMobile
          eventLog={eventLog}
          onClickBack={() => setShowHistory(false)}
        />
      )}
      {hasTransitionImgLoaded && !showHistory && !delayedProps.loading && (
        <div
          data-test-id="top-level-container"
          data-test-player-name={playerNameValue || "NONE"}
          data-test-game-name={gameSessionId || "NONE"}
          data-test-game-state={gameState || "NONE"}
          data-test-deleayed-game-state={delayedProps.gameState || "NONE"}
          data-test-is-expired={delayedProps.isExpired}
        >
          {canShowHistory && (
            <OpenGameHistoryButton onClick={() => setShowHistory(true)} />
          )}
          <Content {...contentProps} eventLog={eventLog} />
        </div>
      )}
    </>
  );
};
export default Home;
